import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { API } from "@/App";
import Navbar from "@/components/Navbar";
import AdvertBanner from "@/components/AdvertBanner";
import Footer from "@/components/Footer";
import PoolResultDisplay from "@/components/PoolResultDisplay";
import axios from "axios";
import { ArrowLeft, Clock, ShoppingCart } from "lucide-react";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const catLabel = { blog: "Blog", free_pick: "Free Pick", pool_draws: "Pool Draws", pool_result: "Pool Result" };
const catClass = { blog: "cat-blog", free_pick: "cat-free_pick", pool_draws: "cat-pool_draws", pool_result: "cat-pool_result" };

const PostPage = () => {
  const { postId } = useParams();
  const navigate = useNavigate();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API}/posts/${postId}`).then(r => setPost(r.data)).catch(() => navigate("/")).finally(() => setLoading(false));
  }, [postId, navigate]);

  const getImageUrl = (url) => {
    if (!url) return null;
    if (url.startsWith("/api")) return `${BACKEND}${url}`;
    return url;
  };

  if (loading) return <div className="min-h-screen flex items-center justify-center"><div className="animate-spin rounded-full h-10 w-10 border-t-2 border-b-2 border-green-600"></div></div>;
  if (!post) return null;

  const backPath = post.category === "pool_result" ? "/pool/results" : post.category === "pool_draws" ? "/pool/draws" : "/";
  const backLabel = post.category === "pool_result" ? "Pool Results" : post.category === "pool_draws" ? "Pool Draws" : "Home";

  return (
    <div className="min-h-screen bg-[#FAFBFC]" data-testid="post-page">
      <Navbar />
      <div className="pt-28 pb-16 px-4">
        <AdvertBanner />
        <article className="max-w-3xl mx-auto">
          <Button variant="ghost" className="mb-6 text-gray-500" onClick={() => navigate(backPath)} data-testid="back-btn">
            <ArrowLeft className="h-4 w-4 mr-2" /> Back to {backLabel}
          </Button>
          <div className="flex items-center gap-2 mb-4 flex-wrap">
            <Badge className={`${catClass[post.category] || "cat-blog"} text-xs`}>
              {catLabel[post.category] || "Blog"}
            </Badge>
            {post.week_number && <Badge className={`coupon-${post.coupon_color || "brown"} text-xs`}>Week {post.week_number}</Badge>}
            {post.season && <Badge variant="outline" className="text-xs text-gray-700 border-gray-300">{post.season} Pools</Badge>}
            {post.tags?.map(t => <Badge key={t} variant="outline" className="text-xs text-gray-700 border-gray-300">{t}</Badge>)}
          </div>
          <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">{post.title}</h1>
          <div className="flex items-center text-sm text-gray-500 mb-8">
            <Clock className="h-4 w-4 mr-1.5" />
            {new Date(post.created_at).toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric", year: "numeric" })}
          </div>
          {post.image_url && <img src={getImageUrl(post.image_url)} alt={post.title} className="w-full rounded-xl mb-8 object-cover max-h-96" />}
          {post.category === "pool_result" ? (
            <PoolResultDisplay post={post} />
          ) : (
            <div
              className="prose prose-lg max-w-none text-gray-700 leading-relaxed post-content"
              dangerouslySetInnerHTML={{ __html: post.content }}
            />
          )}
          <div className="mt-10 border-t border-gray-200 pt-8 text-center">
            <Button
              size="lg"
              onClick={() => navigate("/buy")}
              className="bg-amber-500 hover:bg-amber-600 text-gray-900 font-bold rounded-full px-8 h-12"
              data-testid="post-buy-btn"
            >
              <ShoppingCart className="h-5 w-5 mr-2" /> Buy Fixed Games
            </Button>
          </div>
        </article>
      </div>
      <Footer />
    </div>
  );
};

export default PostPage;
